/**
 * Help System — single floating button + slide-up panel.
 *
 * Purpose: every staff member (office, estimator, scheduler, crew) can ask
 * "how do I…" at any time and get a short, plain-English answer.
 *
 * What this is NOT: a documentation site, an AI assistant, a chatbot, a
 * search engine over the codebase, or a feature.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  HelpCircle, X, Search, ChevronRight, ChevronLeft,
  Phone, Clipboard, Home as RoofIcon, FileText, Send, Banknote,
  Calendar, Check, Receipt, Camera, Search as SearchIcon,
} from "lucide-react";
import { HELP_GUIDES } from "../lib/help-guides";

const ICONS = {
  Phone, Clipboard, Roof: RoofIcon, FileText, Send, Banknote,
  Calendar, Check, Receipt, Camera, Search: SearchIcon,
};

export default function HelpSystem() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);  // active guide id
  const [q, setQ] = useState("");

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (active) setActive(null);
        else if (open) setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, active]);

  // Filter guides by simple keyword + title match
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return HELP_GUIDES;
    return HELP_GUIDES.filter((g) => {
      const hay = (
        g.title + " " + g.when + " " +
        (g.keywords || []).join(" ")
      ).toLowerCase();
      return hay.includes(needle);
    });
  }, [q]);

  const guide = active ? HELP_GUIDES.find((g) => g.id === active) : null;

  return (
    <>
      {/* Floating button — bottom-right, every page */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="help-button"
        aria-label="Help — how do I do this?"
        className="fixed flex items-center gap-2 px-4 py-3 rounded-full shadow-lg transition-transform hover:scale-105 active:scale-95 z-[1000]"
        style={{
          right: 24, bottom: 24,
          background: "var(--accent, #1B9A5C)",
          color: "#fff",
          fontSize: 13, fontWeight: 700,
          letterSpacing: 0.3,
          border: "1px solid rgba(0,0,0,0.08)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.12)",
        }}
      >
        <HelpCircle size={16} />
        Help
      </button>

      {/* Backdrop + slide-up panel */}
      {open && (
        <div
          data-testid="help-panel"
          className="fixed inset-0 z-[1001] flex items-end md:items-center md:justify-end"
          style={{ background: "rgba(15,15,18,0.45)", backdropFilter: "blur(2px)" }}
          onClick={() => { setOpen(false); setActive(null); }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full md:w-[460px] md:h-[640px] md:mr-6 md:rounded-xl rounded-t-xl overflow-hidden flex flex-col"
            style={{
              background: "var(--surface-1, #fff)",
              border: "1px solid var(--line, #e6e6ea)",
              boxShadow: "0 24px 64px rgba(0,0,0,0.28)",
              maxHeight: "82vh",
            }}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4"
                  style={{ borderBottom: "1px solid var(--line, #ececef)" }}>
              {active ? (
                <button
                  type="button"
                  onClick={() => setActive(null)}
                  data-testid="help-back"
                  className="p-1 rounded hover:bg-black/5"
                  aria-label="Back to all guides"
                >
                  <ChevronLeft size={18} />
                </button>
              ) : (
                <HelpCircle size={18} style={{ color: "var(--accent, #1B9A5C)" }} />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-extrabold uppercase tracking-[0.18em]"
                      style={{ color: "var(--ink-muted, #777)" }}>
                  How do I…
                </div>
                <div className="text-[15px] font-bold leading-tight truncate"
                      style={{ color: "var(--ink, #111)" }}>
                  {guide ? guide.title : "Short guides for everyday tasks"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setOpen(false); setActive(null); }}
                data-testid="help-close"
                className="p-1 rounded hover:bg-black/5"
                aria-label="Close help"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            {!guide && (
              <>
                <div className="px-5 pt-4 pb-2">
                  <label className="flex items-center gap-2 px-3 py-2 rounded-md"
                          style={{ background: "var(--bg-elev-2, #f4f4f6)",
                                  border: "1px solid var(--line, #e6e6ea)" }}>
                    <Search size={14} style={{ color: "var(--ink-muted, #888)" }} />
                    <input
                      type="text"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Try: deposit, photo, schedule, customer…"
                      data-testid="help-search"
                      className="flex-1 bg-transparent outline-none text-[13px]"
                    />
                  </label>
                </div>
                <div className="flex-1 overflow-y-auto px-3 pb-4">
                  {filtered.length === 0 && (
                    <div className="text-center text-[12px] py-10"
                          style={{ color: "var(--ink-muted, #888)" }}>
                      No guide matches &ldquo;{q}&rdquo;. Try a simpler word like
                      &ldquo;deposit&rdquo; or &ldquo;site visit&rdquo;.
                    </div>
                  )}
                  {filtered.map((g) => {
                    const Icon = ICONS[g.icon] || HelpCircle;
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => setActive(g.id)}
                        data-testid={`help-guide-${g.id}`}
                        className="w-full text-left rounded-lg px-3 py-3 mb-1 flex items-start gap-3 transition-colors"
                        style={{
                          background: "transparent",
                          border: "1px solid transparent",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "var(--bg-elev-2, #f4f4f6)";
                          e.currentTarget.style.borderColor = "var(--line, #e6e6ea)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.borderColor = "transparent";
                        }}
                      >
                        <span
                          className="flex items-center justify-center shrink-0"
                          style={{
                            width: 32, height: 32, borderRadius: 8,
                            background: "rgba(27,154,92,0.12)",
                            color: "var(--accent, #1B9A5C)",
                          }}
                        >
                          <Icon size={14} />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-[13.5px] font-bold leading-tight"
                                style={{ color: "var(--ink, #111)" }}>
                            {g.title}
                          </span>
                          <span className="block text-[11.5px] mt-0.5"
                                style={{ color: "var(--ink-muted, #777)" }}>
                            {g.when}
                          </span>
                        </span>
                        <ChevronRight size={16} style={{ color: "var(--ink-faint, #bbb)", marginTop: 6 }} />
                      </button>
                    );
                  })}
                </div>
                <div className="px-5 py-3 text-[10.5px] text-center"
                      style={{
                        color: "var(--ink-muted, #888)",
                        borderTop: "1px solid var(--line, #ececef)",
                      }}>
                  Press <kbd className="px-1 rounded border" style={{ borderColor: "var(--line, #ddd)" }}>Esc</kbd> to close.
                  Can&rsquo;t find what you need? Ask whoever showed you the system on day one.
                </div>
              </>
            )}

            {/* Single guide view */}
            {guide && (
              <div className="flex-1 overflow-y-auto px-5 py-5">
                <div className="text-[12.5px] mb-4 px-3 py-2 rounded"
                      style={{
                        background: "rgba(27,154,92,0.08)",
                        color: "var(--ink, #111)",
                        borderLeft: "3px solid var(--accent, #1B9A5C)",
                      }}>
                  <strong>When to use this:</strong> {guide.when}
                </div>
                <ol className="space-y-3">
                  {guide.steps.map((s, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3"
                      data-testid={`help-step-${guide.id}-${i + 1}`}
                    >
                      <span
                        className="shrink-0 flex items-center justify-center"
                        style={{
                          width: 26, height: 26, borderRadius: 999,
                          background: "var(--accent, #1B9A5C)",
                          color: "#fff",
                          fontSize: 12, fontWeight: 800,
                        }}
                      >
                        {i + 1}
                      </span>
                      <span className="text-[13.5px] leading-relaxed pt-0.5"
                            style={{ color: "var(--ink, #222)" }}>
                        {s}
                      </span>
                    </li>
                  ))}
                </ol>
                <div className="mt-6 pt-4 text-[11px]"
                      style={{
                        color: "var(--ink-muted, #888)",
                        borderTop: "1px solid var(--line, #ececef)",
                      }}>
                  That&rsquo;s it. If something on screen doesn&rsquo;t match this guide,
                  it usually means the job is at a different step &mdash; go back
                  to the job&rsquo;s &ldquo;What to do next&rdquo; panel and follow that.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
